import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Typography } from '@totalsoft/rocket-ui';
import debounce from 'debounce-promise';
import assetHierarchyFunctions from '../assetHierarchyFunctions';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Box, Grid, Tooltip } from '@mui/material';
import { loadAssets } from 'features/asset/hierarchy/actionCreators';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import { getAssetHierarchyData } from '../selectors';
import { loadAssetHierarchyLevels } from '../actionCreators';
import { SEARCH_BIT, TP_NUM } from 'constants/inputTypes';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

const AssetSearchComponent = ({
  actions,
  onPropertyChange,
  hierarchy,
  hierarchyLevels,
  validationInfo,
  t,
  rights,
  assetTypes,
  assetTypeId,
  buildYearAndMonth,
  showQuantity,
  quantity,
  quantityDisabled,
  quantityTooltip,
  quantityError,
  withIndividualAssetData,
  processing,
  addToRefs
}) => {
  const loadAssetsDebounced = useRef(debounce(actions.loadAssets, 1000));
  const hierarchyLevelsLoaded = hierarchyLevels ? hierarchyLevels.loaded : false;

  useEffect(() => {
    if (!hierarchyLevels || hierarchyLevels.loaded === undefined || hierarchyLevels.loaded || !assetTypeId) return;
    actions.loadAssetHierarchyLevels(assetTypeId, rights);
  }, [hierarchyLevelsLoaded, actions, assetTypeId, hierarchyLevels, rights]);

  const filterOptions = useCallback(options => {
    return options;
  }, []);

  const loadFilteredAssets = useCallback(
    input => {
      if (!input || input.length < 2) {
        return Promise.resolve([]);
      }

      return loadAssetsDebounced.current(input, assetTypeId, buildYearAndMonth).then(data => {
        return data;
      });
    },
    [assetTypeId, buildYearAndMonth]
  );

  const { onLevelChange } = assetHierarchyFunctions(hierarchyLevels, hierarchy, onPropertyChange, rights);

  const getHierarchyItemCallback = useCallback(() => {
    if (hierarchyLevels) {
      const { getHierarchyItem } = assetHierarchyFunctions(hierarchyLevels, hierarchy, onPropertyChange, rights);
      return getHierarchyItem();
    }
  }, [hierarchyLevels, hierarchy, onPropertyChange, rights]);

  const item = getHierarchyItemCallback();

  const handleAssetTypeChange = useCallback(
    value => {
      const assetType = assetTypes.find(x => x.assetTypeId === value);
      if (!assetType) return;
      onPropertyChange('assetTypeId', assetType.assetTypeId);
      onPropertyChange('assetTypeName', assetType.name);
      onPropertyChange('itemId', assetType.itemId);
    },
    [assetTypes, onPropertyChange]
  );

  const handleQuantityChange = useCallback(value => onPropertyChange('quantity')(value), [onPropertyChange]);

  const renderAssetOption = useCallback((props, option, { inputValue }) => {
    const matches = match(option.name, inputValue);
    const parts = parse(option.name, matches);

    return (
      <li {...props}>
        <Box px={1} py={0.5}>
          {parts.map((part, index) => (
            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
              {part.text}
            </span>
          ))}
        </Box>
      </li>
    );
  }, []);

  return (
    <>
      <Grid item xs={4}>
        <Autocomplete
          id={'offerAssetCard_info_assetType'}
          fullWidth
          required
          error={Boolean(validationInfo?.assetTypeIdError)}
          helperText={validationInfo?.assetTypeIdError}
          isSearchable={false}
          isClearable={false}
          valueKey='assetTypeId'
          simpleValue
          value={assetTypeId || ''}
          onChange={handleAssetTypeChange}
          label={t('OfferEdit.Asset.Info.AssetType')}
          options={assetTypes}
          disabled={rights.readOnlyFull || !rights.financedAssetWrite}
        />
      </Grid>
      {item && (
        <Grid
          item
          xs
          id={t('OfferEdit.Asset.Info.Asset')}
          ref={ref => {
            if (addToRefs) addToRefs(ref, t('OfferEdit.Asset.Info.Asset'), SEARCH_BIT);
          }}
        >
          <Autocomplete
            isSearchable
            isClearable
            value={item}
            valueKey='id'
            label={t('OfferEdit.Asset.Info.Asset')}
            loadOptions={loadFilteredAssets}
            filterOptions={filterOptions}
            onChange={selection => onLevelChange(selection, item.level, item.label, item.triggers)}
            disabled={rights.readOnlyFull || !rights.financedAssetWrite}
            renderOption={renderAssetOption}
          />
        </Grid>
      )}
      {showQuantity && (
        <>
          <Grid item container xs={0.5} justifyContent='center' mt={2.5}>
            <Typography emphasis='bold' variant='body2'>
              {'X'}
            </Typography>
          </Grid>
          <Tooltip title={quantityTooltip}>
            <Grid item xs={2} md={1.5} ref={ref => addToRefs(ref, t('OfferEdit.Asset.Financial.Quantity'), TP_NUM)}>
              <TextField
                id={'offerAssetCard_financial_quantity'}
                fullWidth
                required
                isNumeric
                error={Boolean(quantityError)}
                helperText={quantityError}
                value={quantity}
                onChange={handleQuantityChange}
                debounceBy={400}
                label={t('OfferEdit.Asset.Financial.Quantity')}
                inputProps={{ decimalScale: 0 }}
                disabled={rights.readOnlyFull || !rights.financedAssetWrite || withIndividualAssetData || processing || quantityDisabled}
              />
            </Grid>
          </Tooltip>
        </>
      )}
    </>
  );
};

AssetSearchComponent.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  assetTypes: PropTypes.array.isRequired,
  assetTypeId: PropTypes.number,
  hierarchyLevels: PropTypes.array.isRequired,
  hierarchy: PropTypes.object.isRequired,
  validationInfo: PropTypes.object,
  configurator: PropTypes.bool,
  configuratorHierarchy: PropTypes.array,
  buildYearAndMonth: PropTypes.string,
  showQuantity: PropTypes.bool,
  quantity: PropTypes.number,
  quantityDisabled: PropTypes.bool,
  quantityTooltip: PropTypes.string,
  quantityError: PropTypes.string,
  withIndividualAssetData: PropTypes.bool,
  processing: PropTypes.bool,
  addToRefs: PropTypes.func
};

AssetSearchComponent.defaultProps = {
  showQuantity: false,
  quantityDisabled: false,
  quantityTooltip: ''
};

function mapStateToProps(state, ownProps) {
  let { assetTypeId, hierarchy, hierarchyLevels, validationInfo } = getAssetHierarchyData(state);
  const assetTypes = state.assetData.assetTypes;

  if (ownProps.configurator) hierarchyLevels = ownProps.configuratorHierarchy;

  return {
    assetTypeId,
    assetTypes,
    hierarchy,
    hierarchyLevels,
    validationInfo
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadAssets, loadAssetHierarchyLevels }, dispatch)
  };
}

const MemoizedAssetSearchComponent = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(withRights(AssetSearchComponent));

const ForwardedAssetSearchComponent = forwardRef((props, ref) => <MemoizedAssetSearchComponent {...props} forwardedRef={ref} />);

ForwardedAssetSearchComponent.displayName = 'ForwardedAssetSearchComponent';

export default ForwardedAssetSearchComponent;
